import React, { useState } from 'react';
import { Input, Button, Table, Alert, Tag, message, Space, Card } from 'antd';
import { ImportOutlined } from '@ant-design/icons';
import { parseLarkText } from '@/services/parser';
import { useHealthStore } from '@/stores/useHealthStore';
import type { ParsedLarkData } from '@/types';

interface LarkImportProps {
  onSuccess?: () => void;
}

const LarkImport: React.FC<LarkImportProps> = ({ onSuccess }) => {
  const [text, setText] = useState('');
  const [parsedData, setParsedData] = useState<ParsedLarkData[]>([]);
  const [loading, setLoading] = useState(false);
  const importLarkData = useHealthStore(state => state.importLarkData);

  const handleParse = () => {
    if (!text.trim()) {
      message.warning('请输入飞书聊天记录');
      return;
    }

    const data = parseLarkText(text);
    setParsedData(data);

    if (data.length === 0) {
      message.warning('未能解析出有效数据，请检查格式');
    } else {
      message.success(`成功解析 ${data.length} 条记录`);
    }
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      message.warning('请先解析数据');
      return;
    }

    setLoading(true);
    try {
      const result = await importLarkData(parsedData);
      message.success(`导入完成：成功 ${result.success} 条，跳过 ${result.skipped} 条（重复）`);
      setText('');
      setParsedData([]);
      onSuccess?.();
    } catch (error) {
      message.error('导入失败');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date'
    },
    {
      title: '血压',
      key: 'bp',
      render: (_: any, record: ParsedLarkData) => (
        record.systolic && record.diastolic ? (
          <Tag color="red">{record.systolic}/{record.diastolic}</Tag>
        ) : '-'
      )
    },
    {
      title: '血糖',
      key: 'bs',
      render: (_: any, record: ParsedLarkData) => (
        record.bloodSugar ? (
          <Tag color="blue">{record.bloodSugar}</Tag>
        ) : '-'
      )
    }
  ];

  return (
    <div>
      <Alert
        message="飞书聊天记录导入"
        description={
          <div>
            <p>1. 在飞书中长按消息 → 多选 → 复制</p>
            <p>2. 粘贴到下方文本框</p>
            <p>3. 支持格式：<code>0306 141/89 6.2</code>（月日 高压/低压 血糖）</p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Input.TextArea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="粘贴飞书聊天记录...
例如：
0306 141/89 6.2
0305 138/92 5.8
0304 135/88"
        rows={6}
        style={{ marginBottom: 16 }}
      />

      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleParse}>
          解析数据
        </Button>
        <Button
          type="primary"
          icon={<ImportOutlined />}
          onClick={handleImport}
          loading={loading}
          disabled={parsedData.length === 0}
        >
          导入 {parsedData.length > 0 && `(${parsedData.length}条)`}
        </Button>
      </Space>

      {parsedData.length > 0 && (
        <Card title="预览解析结果" size="small">
          <Table
            dataSource={parsedData}
            columns={columns}
            rowKey={(record, index) => `${record.date}-${index}`}
            pagination={false}
            size="small"
          />
        </Card>
      )}
    </div>
  );
};

export default LarkImport;
